import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Label,
} from 'recharts';

import { VARIABLES } from '@app/components/common/constants';
import { BaseForecastModel, FORECAST_MODELS } from '@app/common/static-data/models';
import { useTheme } from '@app/theme/ThemeContext';
import { PerformanceMetric } from '@app/api/live-benchmarking/types';

interface PerformanceChartProps {
  data: PerformanceMetric[];
  selectedVariable: string;
  selectedMetric: 'rmse' | 'mbe';
  latestForecastTime: Date;
}

export function PerformanceChart({
  data,
  selectedVariable,
  selectedMetric,
  latestForecastTime,
}: PerformanceChartProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#E5E7EB' : '#666666';

  // Filter data for the latest forecast time
  const latestData = data.filter(
    (metric) => metric.start_time.getTime() === latestForecastTime.getTime(),
  );

  const variable = VARIABLES.find((v) => v.juaId === selectedVariable);
  if (!variable) return null;

  // Sort data by prediction_timedelta
  const sortedData = [...latestData].sort(
    (a, b) => a.prediction_timedelta - b.prediction_timedelta,
  );

  // Transform data for recharts
  const chartData = sortedData.reduce((acc: any[], curr) => {
    const existingPoint = acc.find((p) => p.timedelta === curr.prediction_timedelta);

    const value = curr[selectedVariable as keyof PerformanceMetric] as number;

    if (existingPoint) {
      existingPoint[`value_${curr.model}`] = value;
    } else {
      acc.push({
        timedelta: curr.prediction_timedelta,
        [`value_${curr.model}`]: value,
      });
    }
    return acc;
  }, []);

  const metricLabel = selectedMetric.toUpperCase();
  const availableModels = [...new Set(data.map((d) => d.model))].map((model) =>
    FORECAST_MODELS.find((m: BaseForecastModel) => m.id === model)!,
  );

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !variable) return null;

    return (
      <div
        className={`${isDark ? 'bg-theme-dark-bg border-theme-dark-sidebar' : 'bg-white border-gray-200'} p-3 border rounded-lg`}
      >
        <p
          className={`text-sm font-medium mb-2 ${isDark ? 'text-theme-dark-text-primary' : 'text-gray-900'}`}
        >
          {`${label}h`}
        </p>
        {payload.map((entry: any, index: number) => {
          const modelId = entry.dataKey.replace(`value_`, '');
          const model = availableModels.find((m: BaseForecastModel) => m.id === modelId)!;

          return (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {`${model.label}: ${entry.value.toFixed(2)} ${variable.displayUnit}`}
            </p>
          );
        })}
      </div>
    );
  };

  const getModelColor = (model: BaseForecastModel, index: number, total: number) => {
    // Special colors for EPT models
    if (model.id.includes('ept')) {
      // For EPT models, use purple/pink hues (280-320)
      const hue = model.id.includes('early') ? 320 : 280; // pink for early, purple for regular
      return `hsl(${hue}, 70%, 45%)`;
    }

    // For other models, distribute colors across the full spectrum
    // Skip the purple/pink range (280-320) to avoid confusion with EPT models
    let hue;
    if (index === 0)
      hue = 0; // Red
    else if (index === 1)
      hue = 120; // Green
    else if (index === 2)
      hue = 200; // Blue
    else {
      // For remaining models, distribute in the ranges 20-260, avoiding purple/pink
      const normalizedIndex = (index - 3) / (total - 3);
      hue = 20 + normalizedIndex * 240;
    }
    return `hsl(${hue}, 85%, 45%)`;
  };

  return (
    <div className="relative">
      <div className="overflow-x-auto min-h-[500px] -mx-2 sm:mx-0">
        <div className="min-w-[800px] h-[500px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 30, right: 20, left: 30, bottom: 40 }}>
              <Legend
                verticalAlign="top"
                height={36}
                wrapperStyle={{
                  color: axisColor,
                  paddingTop: '0px',
                }}
              />

              <XAxis
                dataKey="timedelta"
                tickFormatter={(value) => `${value}h`}
                tick={{ fontSize: 12, fill: axisColor }}
              >
                <Label
                  value="Forecast Lead Time [hours]"
                  position="bottom"
                  style={{ textAnchor: 'middle', fill: axisColor }}
                  offset={20}
                />
              </XAxis>

              <YAxis
                yAxisId="main"
                orientation="left"
                tickFormatter={(x) => x.toLocaleString()}
                tick={{ fontSize: 12, fill: axisColor }}
              >
                <Label
                  angle={270}
                  position="left"
                  style={{ textAnchor: 'middle', fill: axisColor }}
                  offset={15}
                >
                  {`${metricLabel} [${variable.displayUnit}]`}
                </Label>
              </YAxis>

              <Tooltip content={CustomTooltip} />

              {availableModels.map((model, index) => (
                <Line
                  key={`value_${model.id}`}
                  type="monotone"
                  dataKey={`value_${model.id}`}
                  name={model.label}
                  yAxisId="main"
                  dot={false}
                  stroke={getModelColor(model, index, availableModels.length)}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
